// Petty cash (P4.7; 01-SPEC §5 "Petty cash", D63, D65). Procurement decides on a request in
// Sourcing to buy it with petty cash instead of an RFQ; the buyer pays, then claims it back from
// the request with the receipt, up to the Admin limit per claim (default SAR 5,000). Finance
// approves (or returns or rejects) and reimburses. The requester never sees the claim. Pure rules,
// unit-tested; vertical, entity and currency come from the request. Amounts are minor units.

import { MAX_UPLOAD_BYTES } from '../../shared/files';
import { z } from 'zod';
import { hasRole, sameUpn, type Actor } from '../rules/permissions';
import { allow, deny, type Decision } from '../rules/decision';

export const PC_NO = /^[A-Z]{3}-PC-\d{4,}-\d{4}$/;

export const PC_STATUSES = [
  'Draft',
  'WithFinance',
  'Returned',
  'Approved',
  'Reimbursed',
  'Rejected',
  /** A draft dropped when Procurement went back to an RFQ (D65). */
  'Cancelled',
] as const;
export type PcStatus = (typeof PC_STATUSES)[number];

/** Default limit per claim in major units (01-SPEC: SAR 5,000); other currencies need a setting. */
export const DEFAULT_LIMITS: Readonly<Record<string, number>> = { SAR: 5000 };

export const MAX_RECEIPT_BYTES = MAX_UPLOAD_BYTES;
const DATE = /^\d{4}-\d{2}-\d{2}$/;

const text = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .regex(/^[^<>]*$/, 'Remove < and >');

const twoDecimals = (v: number) => Math.abs(v * 100 - Math.round(v * 100)) < 1e-6;

/** What the buyer types; office, currency, category and cost centre come from the request. */
export const claimSchema = z.strictObject({
  description: text(200).min(1, 'Required'),
  /** Major units, incl. VAT. */
  amount: z
    .number()
    .positive('More than 0')
    .max(10_000_000)
    .refine(twoDecimals, 'Up to 2 decimals'),
  paidOn: z.string().regex(DATE, 'Pick a date'),
  paidTo: text(120).min(1, 'Required'),
});
export type ClaimInput = z.infer<typeof claimSchema>;

export const createClaimSchema = z.strictObject({ claim: claimSchema });
export const updateClaimSchema = z.strictObject({
  etag: z.string().min(1).max(200),
  claim: claimSchema,
  /** Send to Finance after saving (needs the receipt and the amount within the limit). */
  submit: z.boolean(),
});

export const PC_ACTIONS = ['approve', 'return', 'reject', 'reimburse'] as const;
export type PcAction = (typeof PC_ACTIONS)[number];

const remark = text(1000).min(1, 'Say why');
export const pcFinanceSchema = z.discriminatedUnion('action', [
  z.strictObject({ action: z.literal('approve'), etag: z.string().min(1).max(200) }),
  z.strictObject({ action: z.literal('return'), etag: z.string().min(1).max(200), remark }),
  z.strictObject({ action: z.literal('reject'), etag: z.string().min(1).max(200), remark }),
  z.strictObject({
    action: z.literal('reimburse'),
    etag: z.string().min(1).max(200),
    reference: z
      .string()
      .trim()
      .min(1, 'Enter the reimbursement reference')
      .max(60)
      .regex(/^[\p{L}\p{N} ./_#-]+$/u, 'Letters, digits and . / _ # - only'),
    reimbursedOn: z.string().regex(DATE, 'Pick a date'),
  }),
]);
export type PcFinanceInput = z.infer<typeof pcFinanceSchema>;

/** The limit for a currency in minor units, or 0 when petty cash is not set up for it. */
export const limitMinor = (limitMajor: number) => Math.max(0, Math.round(limitMajor * 100));

export type LimitCheck =
  { ok: true } | { ok: false; reason: 'over_limit' | 'no_limit'; message: string };

/** Above the limit (or no limit set for the currency) → raise a purchase request instead. */
export function checkLimit(amount: number, limit: number, currency: string): LimitCheck {
  if (limit <= 0) {
    return {
      ok: false,
      reason: 'no_limit',
      message: `Petty cash is not set up for ${currency}. Use an RFQ instead.`,
    };
  }
  if (amount > limit) {
    const major = new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(limit / 100);
    return {
      ok: false,
      reason: 'over_limit',
      message: `Above the petty cash limit of ${currency} ${major}. Use an RFQ instead.`,
    };
  }
  return { ok: true };
}

interface PcRule {
  action: PcAction | 'submit';
  from: readonly PcStatus[];
  to: PcStatus;
}

const RULES: readonly PcRule[] = [
  { action: 'submit', from: ['Draft', 'Returned'], to: 'WithFinance' },
  { action: 'approve', from: ['WithFinance'], to: 'Approved' },
  { action: 'return', from: ['WithFinance'], to: 'Returned' },
  { action: 'reject', from: ['WithFinance'], to: 'Rejected' },
  { action: 'reimburse', from: ['Approved'], to: 'Reimbursed' },
];

export interface PcActionInput {
  action: PcAction | 'submit';
  actor: Actor;
  /** requesterUpn is the claimant (the buyer); prRequesterUpn who raised the request. */
  claim: { status: PcStatus; requesterUpn: string; prRequesterUpn?: string | undefined };
  remark?: string | undefined;
}

/** A claim still in play for its request (not rejected or dropped). */
export const isLiveClaim = (s: PcStatus) => s !== 'Rejected' && s !== 'Cancelled';

/** Submit: the claimant only. Finance actions: Finance only, never on their own claim nor on a
 * claim for a request they raised. */
export function applyPcAction(input: PcActionInput): Decision<PcStatus> {
  const { action, actor, claim } = input;
  const rule = RULES.find((r) => r.action === action && r.from.includes(claim.status));
  if (!rule) return deny('invalid_transition', `A claim in ${claim.status} cannot be "${action}".`);
  if (action === 'submit') {
    return sameUpn(actor.upn, claim.requesterUpn)
      ? allow(rule.to)
      : deny('forbidden', 'Only the claimant can send this claim.');
  }
  if (!hasRole(actor, 'Finance')) return deny('forbidden', 'This needs the Finance role.');
  if (sameUpn(actor.upn, claim.requesterUpn)) {
    return deny('self_approval', 'You cannot decide your own petty cash claim.');
  }
  if (sameUpn(actor.upn, claim.prRequesterUpn)) {
    return deny('self_approval', 'You cannot decide a claim for a request you raised.');
  }
  if ((action === 'return' || action === 'reject') && !input.remark?.trim()) {
    return deny('reason_required', 'A remark is required.');
  }
  return allow(rule.to);
}

/** The Finance actions the viewer may take now (buttons); the broker checks again. */
export function pcActionsFor(
  actor: Actor,
  claim: { status: PcStatus; requesterUpn: string; prRequesterUpn?: string | undefined },
): PcAction[] {
  return PC_ACTIONS.filter((a) => applyPcAction({ action: a, actor, claim, remark: 'x' }).ok);
}

export interface ClaimView {
  pcNo: string;
  /** The request it pays for ('' for claims made before D65). */
  prNo: string;
  etag: string;
  status: PcStatus;
  description: string;
  currency: string;
  /** Minor units. */
  amount: number;
  paidOn: string;
  category: string;
  paidTo: string;
  office: string;
  entity: string;
  vertical: string;
  requesterUpn: string;
  requesterName: string;
  department: string;
  receipt: string;
  submittedAt: string;
  financeRemark: string;
  financeApprover: string;
  decidedAt: string;
  reimbursedOn: string;
  reimbursementRef: string;
}

export interface ClaimSummary {
  pcNo: string;
  prNo: string;
  status: PcStatus;
  description: string;
  currency: string;
  amount: number;
  paidOn: string;
  requesterName: string;
  submittedAt: string;
}

/** A request Procurement decided to buy with petty cash, not yet claimed. */
export interface WaitingRequest {
  prNo: string;
  title: string;
  requesterName: string;
  department: string;
}

export interface PettyCashBody {
  /** Procurement: the claims they made, and requests set to petty cash without a live claim. */
  mine: ClaimSummary[];
  waiting: WaitingRequest[];
  /** Per-claim limit by currency, minor units (0 = not set up). */
  limits: Record<string, number>;
  /** Finance (and Admin, read-only) see the claims waiting for a decision or a reimbursement. */
  finance: { toApprove: ClaimSummary[]; toReimburse: ClaimSummary[]; canAct: boolean } | null;
}

/** The claim form on a request (`#/requests/<PR>/petty-cash`). */
export interface ClaimFormBody {
  prNo: string;
  title: string;
  currency: string;
  /** Minor units; 0 = petty cash not offered in this currency. */
  limit: number;
  etag: string;
}

export interface ClaimBody {
  claim: ClaimView;
  limit: number;
  /** The claimant may edit and send (Draft or Returned). */
  canEdit: boolean;
  actions: PcAction[];
}
